import { BLOCKER_CATEGORIES, TASK_EVENT_LABELS, type TaskFieldDto } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Group, Image, SimpleGrid, Stack, Text, UnstyledButton } from '@mantine/core';
import { useGallery } from '../../components/PhotoGallery';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { ProtocolsBlock } from './ProtocolsBlock';
import { ReasonDialog } from './common';
import { useState } from 'react';

const PLAN_EVENTS = ['przelozenie', 'zmiana_skladu'];

/** Dane z terenu przy zadaniu: czynności, zdjęcia, notatki, materiały, blokady, zdarzenia do sprawdzenia. */
export function FieldSection({ taskId }: { taskId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['task-field', taskId], queryFn: () => api<TaskFieldDto>(`/tasks/${taskId}/field`) });
  // galeria zdjęć pracy: kliknięcie miniatury otwiera wszystkie wysłane zdjęcia (z czynnością w podpisie)
  const gallery = useGallery(
    (q.data?.photos ?? [])
      .filter((p) => p.uploaded)
      .map((p) => ({
        id: p.id,
        src: `/api/tasks/${taskId}/photos/${p.id}`,
        caption: `${p.authorName} · ${new Date(p.takenAt).toLocaleString('pl-PL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
        detail: p.checklistItemId ? (q.data?.checklist.find((c) => c.id === p.checklistItemId)?.label ?? null) : null,
      })),
  );
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['task-field', taskId] });
    void qc.invalidateQueries({ queryKey: ['task', taskId] });
    void qc.invalidateQueries({ queryKey: ['planning-board'] });
  };
  const resolve = useMutation({
    mutationFn: (v: { id: string; resolution: string }) => api(`/task-blockers/${v.id}/resolve`, { method: 'POST', body: { resolution: v.resolution } }),
    onSuccess: () => {
      notifyOk('Blokada rozwiązana');
      setResolving(null);
      refresh();
    },
    onError: notifyError,
  });
  const [resolving, setResolving] = useState<string | null>(null);
  const review = useMutation({
    mutationFn: (id: number) => api(`/task-events/${id}/review`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Oznaczono jako sprawdzone');
      refresh();
    },
    onError: notifyError,
  });
  const d = q.data;
  if (!d) return null;
  const done = d.checklist.filter((c) => c.done).length;
  const flagged = d.events.filter((e) => e.flagged && !e.reviewed);
  const nothing = !d.protocols.length && !done && !d.notes.length && !d.photos.length && !d.materials.length && !d.blockers.length && !d.events.length;

  return (
    <Stack gap="xs">
      <Text fw={600} size="sm">
        Dane z terenu
      </Text>
      {nothing && (
        <Text size="xs" c="dimmed">
          Ekipa nie przesłała jeszcze danych z aplikacji.
        </Text>
      )}
      {flagged.length > 0 && (
        <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Do sprawdzenia">
          <Stack gap={4}>
            {flagged.map((e) => (
              <Group key={e.id} justify="space-between" wrap="nowrap">
                <Text size="sm">
                  {TASK_EVENT_LABELS[e.type] ?? e.type} — {e.userName ?? '—'}, {formatDateTime(e.at)}
                  {e.message ? `: ${e.message}` : ''}
                </Text>
                {d.can.review && (
                  <Button size="compact-xs" variant="subtle" onClick={() => review.mutate(e.id)}>
                    Oznacz jako sprawdzone
                  </Button>
                )}
              </Group>
            ))}
          </Stack>
        </Alert>
      )}
      {resolving && (
        <ReasonDialog
          title="Rozwiązanie blokady"
          label="Jak rozwiązano blokadę? (informacja dla ekipy)"
          confirm="Rozwiąż — wznów zadanie"
          busy={resolve.isPending}
          onClose={() => setResolving(null)}
          onConfirm={(r) => resolve.mutate({ id: resolving, resolution: r })}
        />
      )}
      <ProtocolsBlock taskId={taskId} protocols={d.protocols} can={d.can} />
      {d.blockers.map((b) => (
        <Alert key={b.id} color={b.resolvedAt ? 'gray' : 'red'} title={`Blokada: ${BLOCKER_CATEGORIES[b.category]}${b.resolvedAt ? ' (rozwiązana)' : ''}`}>
          <Text size="sm">{b.description}</Text>
          <Text size="xs" c="dimmed">
            {b.reportedByName}, {formatDateTime(b.reportedAt)}
            {b.resolution ? ` · rozwiązanie: ${b.resolution}` : ''}
          </Text>
          {!b.resolvedAt && d.can.resolveBlocker && (
            <Button size="xs" mt={6} onClick={() => setResolving(b.id)}>
              Rozwiąż — wznów zadanie
            </Button>
          )}
        </Alert>
      ))}
      <Text size="sm">
        Czynności: <b>{done}</b> z {d.checklist.length}
      </Text>
      <Stack gap={2}>
        {d.checklist.map((c, i) => (
          <Stack key={c.id} gap={2}>
          {/* wspólna realizacja kilku branż — nagłówek branży */}
          {new Set(d.checklist.map((x) => x.tradeCode)).size > 1 && c.tradeName && c.tradeCode !== d.checklist[i - 1]?.tradeCode && (
            <Text size="xs" fw={700} c="eko" tt="uppercase" mt={4}>
              {c.tradeName}
            </Text>
          )}
          <Group gap={6} wrap="nowrap">
            {c.done ? <IconCheck size={14} color="var(--mantine-color-green-7)" /> : <IconX size={14} color="var(--mantine-color-gray-5)" />}
            <Text size="xs">
              {c.label}
              {c.value ? `: ${c.value}${c.unit ? ` ${c.unit}` : ''}` : ''}
              {c.doneByName ? ` · ${c.doneByName}` : ''}
            </Text>
            {c.flagged && (
              <Badge size="xs" color="orange">
                do sprawdzenia
              </Badge>
            )}
          </Group>
          </Stack>
        ))}
      </Stack>
      {d.photos.length > 0 && (
        <>
          <Text size="sm">Zdjęcia ({d.photos.length})</Text>
          <SimpleGrid cols={{ base: 3, sm: 4 }} spacing={6}>
            {d.photos.map((p) =>
              p.uploaded ? (
                <UnstyledButton key={p.id} onClick={() => gallery.open(p.id)} aria-label={`Otwórz galerię: zdjęcie ${p.authorName}`} style={{ display: 'block', cursor: 'zoom-in' }}>
                  <Image src={`/api/tasks/${taskId}/photos/${p.id}`} h={70} fit="cover" radius="sm" alt={`Zdjęcie ${p.authorName}`} />
                </UnstyledButton>
              ) : (
                <Badge key={p.id} variant="light" color="gray" h={70} radius="sm">
                  plik w drodze
                </Badge>
              ),
            )}
          </SimpleGrid>
          {gallery.gallery}
        </>
      )}
      {d.notes.length > 0 && (
        <>
          <Text size="sm">Notatki</Text>
          {d.notes.map((n) => (
            <Text key={n.id} size="xs">
              „{n.body}” — {n.authorName}, {formatDateTime(n.createdAt)}
            </Text>
          ))}
        </>
      )}
      {d.materials.length > 0 && (
        <>
          <Text size="sm">Zużyte materiały</Text>
          {d.materials.map((m) => (
            <Text key={m.id} size="xs">
              {m.name} — {m.quantity} {m.unit} ({m.authorName})
            </Text>
          ))}
        </>
      )}
      {d.events.filter((e) => !e.flagged).length > 0 && (
        <Text size="xs" c="dimmed">
          {d.events
            .filter((e) => !e.flagged)
            // przełożenie i zmiana składu — z treścią (stary → nowy)
            .map((e) => `${TASK_EVENT_LABELS[e.type] ?? e.type}: ${e.userName ?? '—'} ${formatDateTime(e.at)}${PLAN_EVENTS.includes(e.type) && e.message ? ` (${e.message})` : ''}`)
            .join(' · ')}
        </Text>
      )}
    </Stack>
  );
}
